import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { addDays, startOfDay } from '@budget/core';
import { WidgetOverlay } from '../../components/WidgetOverlay';
import { useCalendarEvents, type CalendarEvent } from '../../data/useCalendarEvents';
import { useNow } from '../../data/kioskRefresh';
import { BillsPage } from '../BillsPage';
import { HomeCard, StatusTile } from './HomeCard';

// This week on the family calendar: a Sunday–Saturday strip with a dot on
// each day that has something, and counts for today and the week. Opens the
// budget app's month calendar (bills plus these events) in an overlay.
//
// Only the shared family calendar counts — the expirations and holidays
// calendars are reminders, not things the family is doing.

const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Parse a YYYY-MM-DD all-day date as local midnight (not UTC, which would
// shift the day west of GMT).
function parseLocalDate(s: string): Date {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

// [start, end) in epoch ms. Google's all-day end date is exclusive already;
// a timed event with no length still occupies its start instant.
function eventSpan(e: CalendarEvent): [number, number] {
  if (e.allDay) return [parseLocalDate(e.start).getTime(), parseLocalDate(e.end).getTime()];
  const s = Date.parse(e.start);
  return [s, Math.max(Date.parse(e.end), s + 1)];
}

function overlaps([s, e]: [number, number], from: Date, to: Date): boolean {
  return s < to.getTime() && e > from.getTime();
}

export function CalendarCard() {
  const { events } = useCalendarEvents();
  // Re-renders each minute so "today" rolls over at midnight.
  const now = useNow(60_000);
  const [open, setOpen] = useState(false);

  const week = useMemo(() => {
    const today = startOfDay(now);
    const sunday = addDays(today, -today.getDay());
    const family = events.filter((e) => e.source === 'family').map(eventSpan);
    const days = DAY_LETTERS.map((letter, i) => {
      const day = addDays(sunday, i);
      const next = addDays(day, 1);
      return {
        letter,
        date: day.getDate(),
        isToday: i === today.getDay(),
        isPast: day < today,
        hasEvent: family.some((span) => overlaps(span, day, next)),
      };
    });
    return {
      days,
      todayCount: family.filter((span) => overlaps(span, today, addDays(today, 1))).length,
      weekCount: family.filter((span) => overlaps(span, sunday, addDays(sunday, 7))).length,
    };
    // `now` changes every minute; the week only needs recomputing on a new day.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, now.toDateString()]);

  return (
    <>
      <HomeCard
        title="Calendar"
        wash="--berg-amber-soft"
        ariaLabel={`Calendar: ${week.todayCount} today, ${week.weekCount} this week`}
        onOpen={() => setOpen(true)}
      >
        <span aria-hidden className="relative grid grid-cols-7 gap-px lg:gap-1 -mx-1.5 lg:-mx-2">
          {week.days.map((d, i) => (
            <span
              key={i}
              className={clsx(
                'flex flex-col items-center gap-1.5 py-1.5 lg:py-2 rounded-lg lg:rounded-[10px]',
                d.isToday && 'bg-overlay'
              )}
            >
              <span
                className={clsx(
                  'text-[10px] lg:text-[11px] font-semibold',
                  d.isToday ? 'text-fg' : d.isPast ? 'text-fg-tertiary' : 'text-fg-secondary'
                )}
              >
                {d.letter}
              </span>
              <span
                className={clsx(
                  'hidden lg:block tabular text-[15px] font-semibold',
                  d.isToday ? 'text-fg' : d.isPast ? 'text-fg-tertiary' : 'text-fg-secondary'
                )}
              >
                {d.date}
              </span>
              <span
                className={clsx(
                  'w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full',
                  !d.hasEvent
                    ? ''
                    : d.isPast
                      ? 'bg-subtle border border-border-strong'
                      : 'bg-amber-light dot-glow'
                )}
                style={
                  d.hasEvent && !d.isPast
                    ? { ['--dot-color' as string]: 'var(--berg-amber-light)' }
                    : undefined
                }
              />
            </span>
          ))}
        </span>

        {/* The counts reach into the card's padding, sitting 8px off its edges.
            On a phone they're a size down so they don't crowd the week strip
            in the short card. */}
        <span className="relative grid grid-cols-2 gap-1 lg:gap-2 -mx-2 -mb-2 lg:-mx-3.5 lg:-mb-3.5">
          <Count value={week.todayCount} label="today" highlight />
          <Count value={week.weekCount} label="this week" />
        </span>
      </HomeCard>

      {open &&
        createPortal(
          <WidgetOverlay
            onClose={() => setOpen(false)}
            sourceRect={null}
            label="Calendar"
            header={<span className="text-sm font-semibold">Calendar</span>}
            bodyClassName="flex-1 min-h-0 overflow-y-auto"
          >
            <div className="mx-auto w-full max-w-4xl">
              <BillsPage embedded events={events} />
            </div>
          </WidgetOverlay>,
          document.body
        )}
    </>
  );
}

function Count({
  value,
  label,
  highlight = false,
}: {
  value: number;
  label: string;
  highlight?: boolean;
}) {
  return (
    <StatusTile
      wash={highlight ? '--berg-yellow-soft' : '--berg-neutral-ghost'}
      className="items-start gap-px px-2 py-1 lg:px-3.5 lg:py-2.5"
    >
      <span
        className={clsx(
          'tabular text-base lg:text-[22px] leading-tight font-bold tracking-[-0.02em]',
          highlight ? 'text-yellow' : 'text-fg'
        )}
      >
        {value}
      </span>
      <span
        className={clsx(
          'whitespace-nowrap text-[10px] lg:text-[13px] font-medium',
          highlight ? 'text-yellow-light' : 'text-fg-secondary'
        )}
      >
        {label}
      </span>
    </StatusTile>
  );
}
